import { SearchOutlined } from '@ant-design/icons';
import { Flex, Input } from 'antd';
import { memo, useEffect, useState, type ChangeEvent } from 'react';

import { useDebounceCallback } from '@/hooks/useDebounce';

export type FilteredListingSearchRowProps = {
  placeholder: string;
  /** External value (URL hydration, clear-all). Input syncs when this changes. */
  value: string;
  onDebouncedChange: (value: string) => void;
  debounceMs?: number;
  allowClear?: boolean;
};

/** Single full-width search field for filtered listing shells (list column, not sidebar). */
function FilteredListingSearchRowInner({
  placeholder,
  value,
  onDebouncedChange,
  debounceMs = 300,
  allowClear,
}: FilteredListingSearchRowProps) {
  const [localValue, setLocalValue] = useState(value);
  const debouncedNotify = useDebounceCallback(onDebouncedChange, debounceMs);

  useEffect(() => {
    debouncedNotify.cancel();
    setLocalValue(value);
  }, [value, debouncedNotify]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setLocalValue(next);
    debouncedNotify(next);
  };

  return (
    <Flex vertical>
      <Input
        data-testid="filtered-listing-search"
        placeholder={placeholder}
        value={localValue}
        onChange={handleChange}
        allowClear={allowClear}
        prefix={<SearchOutlined />}
      />
    </Flex>
  );
}

export const FilteredListingSearchRow = memo(FilteredListingSearchRowInner);
